import { CloseOutlined } from '@ant-design/icons';
import { Image, Popover } from 'antd';
import type { CSSProperties } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import styles from './HoverImagePreview.module.css';
import { ImageSourceResolverContext } from './ImageSourceResolver';

function imageSources(...sources: Array<string | undefined>): string[] {
  return [
    ...new Set(
      sources.filter((source): source is string => Boolean(source?.trim())),
    ),
  ];
}

function BrokenImageIcon() {
  return (
    <svg
      className={styles.hoverImageFallbackIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <path d="m13 2-3 7 4 4-3 9M3 16l4-4 3 3m5 1 3-4 3 4" />
      <circle cx="6.5" cy="7" r="1" />
    </svg>
  );
}

function imageFallbackLabel(alt: string, state: '图片加载中' | '图片不存在') {
  const subject = alt.replace(/\s*图片$/, '').trim();
  return subject ? `${subject} ${state}` : state;
}

type ImageResolutionState = {
  sourceKey: string;
  sources: string[];
  originals: string[];
  pending: boolean;
};

type ImageFailureState = {
  sourceKey: string;
  sources: Set<string>;
};

type ImageUnavailableHandler = (
  failedSource?: string,
  manualRetry?: boolean,
) => void | Promise<void>;

function useResolvedImageSources(sources: readonly string[]) {
  const resolver = useContext(ImageSourceResolverContext);
  const normalizedSources = imageSources(...sources);
  const sourceKey = normalizedSources.join('\u0001');
  const immediateSources = normalizedSources.filter(
    (source) => !resolver?.canResolve(source),
  );
  const hasAssetReferences =
    immediateSources.length !== normalizedSources.length;
  const [resolution, setResolution] = useState<ImageResolutionState>(() => ({
    sourceKey,
    sources: immediateSources,
    originals: immediateSources,
    pending: hasAssetReferences,
  }));
  const [failure, setFailure] = useState<ImageFailureState>(() => ({
    sourceKey,
    sources: new Set<string>(),
  }));

  useEffect(() => {
    // Formal preview/thumbnail URLs can be rendered immediately. Avoid a
    // redundant promise + state update for every visible catalog image.
    if (!hasAssetReferences) return;
    let cancelled = false;
    setFailure({ sourceKey, sources: new Set<string>() });

    const resolveSources = async () => {
      const resolved = await Promise.all(
        normalizedSources.map(async (source) => {
          if (!resolver?.canResolve(source)) return source;
          try {
            return await resolver.resolve(source);
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      setResolution({
        sourceKey,
        sources: resolved.filter((source): source is string =>
          Boolean(source?.trim()),
        ),
        originals: normalizedSources.filter((_source, index) =>
          Boolean(resolved[index]?.trim()),
        ),
        pending: false,
      });
    };

    void resolveSources();
    return () => {
      cancelled = true;
    };
  }, [resolver, hasAssetReferences, sourceKey]);

  const renderResolution =
    resolution.sourceKey === sourceKey
      ? resolution
      : {
          sourceKey,
          sources: immediateSources,
          originals: immediateSources,
          pending: hasAssetReferences,
        };
  const failedSources =
    failure.sourceKey === sourceKey ? failure.sources : new Set<string>();

  const markSourceFailed = (source: string) => {
    setFailure((current) => {
      const currentSources =
        current.sourceKey === sourceKey ? current.sources : new Set<string>();
      if (currentSources.has(source)) return current;
      const nextSources = new Set(currentSources);
      nextSources.add(source);
      return { sourceKey, sources: nextSources };
    });
  };

  const clearSourceFailures = () => {
    setFailure({ sourceKey, sources: new Set<string>() });
  };

  return {
    pending: renderResolution.pending,
    sources: renderResolution.sources,
    originals: renderResolution.originals,
    failedSources,
    markSourceFailed,
    clearSourceFailures,
  };
}

export function HoverImagePreview({
  sources,
  imagePresence,
  thumbnailSource,
  thumbnailOnly = false,
  previewDisabled = false,
  onThumbnailClick,
  onImageUnavailable,
  recoveryKey,
  alt,
  frameClassName = '',
  imageClassName,
  width,
  height,
  style,
  imageStyle,
  initialIndex = 0,
  sourceAlts,
}: {
  sources: readonly string[];
  /** Empty is authoritative metadata; omitted means the media has not been established. */
  imagePresence?: 'empty' | 'present';
  thumbnailSource?: string;
  thumbnailOnly?: boolean;
  /** Render the compact image without hover or full-size preview interactions. */
  previewDisabled?: boolean;
  /** Picker cards may expand from the thumbnail while retaining the existing hover preview. */
  onThumbnailClick?: () => void;
  onImageUnavailable?: ImageUnavailableHandler;
  /** Caller scope and media identity for rejecting stale renewal responses. */
  recoveryKey?: string;
  alt: string;
  frameClassName?: string;
  imageClassName?: string;
  width?: number | string;
  height?: number | string;
  style?: CSSProperties;
  imageStyle?: CSSProperties;
  initialIndex?: number;
  sourceAlts?: readonly string[];
}) {
  const {
    pending,
    sources: resolvedSources,
    originals,
    failedSources,
    markSourceFailed,
    clearSourceFailures,
  } = useResolvedImageSources(sources);
  const sourceKey = resolvedSources.join('\u0001');
  const initialResolvedIndex = Math.max(
    0,
    originals.indexOf(sources[initialIndex]),
  );
  const resolver = useContext(ImageSourceResolverContext);
  const [activeIndex, setActiveIndex] = useState(initialResolvedIndex);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const previewTrigger = useRef<HTMLSpanElement | null>(null);
  const [failedCompactSource, setFailedCompactSource] = useState<string | null>(
    null,
  );
  const [retryGeneration, setRetryGeneration] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const recoveryIdentity = `${recoveryKey ?? ''}\u0002${sourceKey}`;
  const recoveryIdentityRef = useRef(recoveryIdentity);
  recoveryIdentityRef.current = recoveryIdentity;
  const recoveryEpoch = useRef(0);
  const recoveryInFlight = useRef(false);
  useEffect(() => {
    recoveryEpoch.current += 1;
    recoveryInFlight.current = false;
    return () => {
      recoveryEpoch.current += 1;
    };
  }, [recoveryIdentity]);
  const autoRetryBudgetKey = recoveryKey ?? alt;
  const autoRetryBudgetKeyRef = useRef(autoRetryBudgetKey);
  const autoRetryAttempted = useRef(false);
  if (autoRetryBudgetKeyRef.current !== autoRetryBudgetKey) {
    autoRetryBudgetKeyRef.current = autoRetryBudgetKey;
    autoRetryAttempted.current = false;
  }
  const onImageUnavailableRef = useRef(onImageUnavailable);
  onImageUnavailableRef.current = onImageUnavailable;
  const activeSourceIndex = Math.min(
    Math.max(0, activeIndex),
    Math.max(0, resolvedSources.length - 1),
  );
  const compactSource =
    thumbnailSource && !resolver?.canResolve(thumbnailSource)
      ? thumbnailSource
      : thumbnailOnly
        ? undefined
        : resolvedSources[initialResolvedIndex];

  const knownEmpty =
    imagePresence === 'empty' &&
    imageSources(...sources, thumbnailSource).length === 0;

  useEffect(() => {
    if (
      !knownEmpty &&
      !pending &&
      (!compactSource || resolvedSources.length === 0)
    )
      void Promise.resolve(
        onImageUnavailableRef.current?.(undefined, false),
      ).catch(() => undefined);
  }, [compactSource, knownEmpty, pending, sourceKey]);

  useEffect(() => {
    setActiveIndex(initialResolvedIndex);
    clearSourceFailures();
    setFailedCompactSource(null);
    setRetryGeneration(0);
    setRetrying(false);
  }, [sourceKey, initialResolvedIndex, recoveryKey]);

  const retryImage = (
    failedSource: string | undefined,
    mode: 'automatic' | 'manual',
  ) => {
    if (mode === 'automatic') {
      if (autoRetryAttempted.current) return;
      autoRetryAttempted.current = true;
    }
    if (recoveryInFlight.current) return;
    const handler = onImageUnavailableRef.current;
    if (mode === 'automatic' && !handler) return;
    const currentIdentity = recoveryIdentity;
    const currentEpoch = recoveryEpoch.current;
    const isCurrent = () =>
      recoveryIdentityRef.current === currentIdentity &&
      recoveryEpoch.current === currentEpoch;
    recoveryInFlight.current = true;
    setRetrying(true);
    void new Promise<void>((resolve) => {
      resolve(handler?.(failedSource, mode === 'manual'));
    })
      .then(
        () => {
          if (!isCurrent()) return;
          clearSourceFailures();
          setFailedCompactSource(null);
          setRetryGeneration((current) => current + 1);
        },
        () => {
          // Keep the manual retry affordance visible after a failed recovery.
        },
      )
      .finally(() => {
        if (isCurrent()) {
          recoveryInFlight.current = false;
          setRetrying(false);
        }
      });
  };

  const handleCompactImageError = (failedSource: string) => {
    if (recoveryIdentityRef.current !== recoveryIdentity) return;
    setFailedCompactSource(failedSource);
    retryImage(failedSource, 'automatic');
  };

  useEffect(() => {
    if (!previewOpen && previewTrigger.current) {
      const trigger = previewTrigger.current;
      const timer = window.setTimeout(() => {
        if (trigger.isConnected) trigger.focus({ preventScroll: true });
        previewTrigger.current = null;
      }, 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [previewOpen]);

  const openPreview = (trigger: HTMLSpanElement) => {
    previewTrigger.current = trigger;
    setHoverOpen(false);
    setPreviewOpen(true);
  };

  const selectPreviewImage = (offset: number) => {
    setActiveIndex(
      (current) =>
        (current + offset + resolvedSources.length) % resolvedSources.length,
    );
  };
  const previewAlt =
    sourceAlts?.[sources.indexOf(originals[activeSourceIndex])] ??
    `预览图片 ${alt}`;
  const frameStyle = { width, height, ...style };
  const fallbackLabel = imageFallbackLabel(
    alt,
    pending ? '图片加载中' : '图片不存在',
  );

  if (
    resolvedSources.length === 0 ||
    !compactSource ||
    failedCompactSource === compactSource
  ) {
    const showBrokenImage =
      !pending &&
      (imagePresence === 'present' ||
        imageSources(...sources, thumbnailSource).length > 0);
    const fallback = (
      <span
        className={`${styles.hoverImageFallback} ${frameClassName}`}
        style={frameStyle}
        data-image-fallback="true"
        role="img"
        aria-label={fallbackLabel}
        data-empty={!showBrokenImage}
      >
        {showBrokenImage ? <BrokenImageIcon /> : '-'}
      </span>
    );
    const retryButton =
      (failedCompactSource === compactSource && compactSource) ||
      (!knownEmpty && !compactSource && onImageUnavailable && !pending) ? (
        <button
          type="button"
          className={styles.hoverImageRetryButton}
          aria-label={`重试加载${alt}`}
          disabled={retrying}
          onClick={(event) => {
            event.stopPropagation();
            retryImage(compactSource, 'manual');
          }}
        >
          {retrying ? '重试中' : '重试'}
        </button>
      ) : null;
    const expandableFallback = (
      <button
        type="button"
        aria-label={fallbackLabel}
        style={{
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
        }}
        onClick={(event) => {
          event.stopPropagation();
          onThumbnailClick?.();
        }}
      >
        {fallback}
      </button>
    );
    return onThumbnailClick && retryButton ? (
      <span className={styles.hoverImageFallbackWithRetry}>
        {expandableFallback}
        {retryButton}
      </span>
    ) : onThumbnailClick ? (
      expandableFallback
    ) : retryButton ? (
      <span className={styles.hoverImageFallbackWithRetry}>
        {fallback}
        {retryButton}
      </span>
    ) : (
      fallback
    );
  }

  if (previewDisabled) {
    return (
      <span
        className={`${styles.hoverImageFrame} ${frameClassName}`}
        style={frameStyle}
        role="img"
        aria-label={alt}
        data-preview-disabled="true"
      >
        <img
          key={`${compactSource}:${retryGeneration}`}
          className={imageClassName}
          style={imageStyle}
          src={compactSource}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => handleCompactImageError(compactSource)}
        />
      </span>
    );
  }

  return (
    <Popover
      trigger="hover"
      open={hoverOpen && !previewOpen}
      onOpenChange={(open) => setHoverOpen(open && !previewOpen)}
      placement="rightTop"
      overlayClassName={styles.hoverImagePopoverOverlay}
      fresh
      destroyOnHidden
      content={
        <div className={styles.hoverImagePopover}>
          <button
            type="button"
            className={styles.hoverImageOpenButton}
            aria-label={`放大查看 ${alt}`}
            onClick={(event) => {
              event.stopPropagation();
              if (triggerRef.current) openPreview(triggerRef.current);
            }}
          >
            {failedSources.has(resolvedSources[activeSourceIndex]) ? (
              <span
                className={`${styles.hoverImagePopoverImage} ${styles.hoverImageFailedPreview}`}
                role="img"
                aria-label={imageFallbackLabel(alt, '图片不存在')}
              >
                <BrokenImageIcon />
              </span>
            ) : (
              <img
                key={`${activeSourceIndex}:${retryGeneration}`}
                className={styles.hoverImagePopoverImage}
                src={resolvedSources[activeSourceIndex]}
                alt={
                  sourceAlts?.[sources.indexOf(originals[activeSourceIndex])] ??
                  alt
                }
                onError={() => {
                  if (recoveryIdentityRef.current !== recoveryIdentity) return;
                  const failedSource = resolvedSources[activeSourceIndex];
                  markSourceFailed(failedSource);
                  retryImage(failedSource, 'automatic');
                }}
              />
            )}
          </button>
          {failedSources.has(resolvedSources[activeSourceIndex]) ? (
            <button
              type="button"
              className={styles.hoverImageRetryButton}
              aria-label={`重试加载${alt}大图`}
              disabled={retrying}
              onClick={(event) => {
                event.stopPropagation();
                retryImage(resolvedSources[activeSourceIndex], 'manual');
              }}
            >
              {retrying ? '重试中' : '重试'}
            </button>
          ) : null}
          {resolvedSources.length > 1 ? (
            <div className={styles.hoverImagePopoverControls}>
              <button
                type="button"
                aria-label="上一张图片"
                onClick={(event) => {
                  event.stopPropagation();
                  selectPreviewImage(-1);
                }}
              >
                ‹
              </button>
              <span aria-live="polite">
                {activeSourceIndex + 1} / {resolvedSources.length}
              </span>
              <button
                type="button"
                aria-label="下一张图片"
                onClick={(event) => {
                  event.stopPropagation();
                  selectPreviewImage(1);
                }}
              >
                ›
              </button>
            </div>
          ) : null}
        </div>
      }
    >
      {/* biome-ignore lint/a11y/useSemanticElements: Picker callers wrap this in a button; retain keyboard access without nesting native buttons. */}
      <span
        ref={triggerRef}
        className={`${styles.hoverImageFrame} ${frameClassName}`}
        style={frameStyle}
        role="button"
        tabIndex={0}
        aria-label={
          onThumbnailClick ? `${alt}，展开或收起成员` : `预览图片 ${alt}`
        }
        aria-haspopup={onThumbnailClick ? undefined : 'dialog'}
        onClick={(event) => {
          event.stopPropagation();
          if (onThumbnailClick) {
            onThumbnailClick();
            return;
          }
          // Preview portals bubble through this trigger, but must not reopen it.
          if (event.currentTarget.contains(event.target as Node))
            openPreview(event.currentTarget);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.stopPropagation();
          if (event.target !== event.currentTarget) return;
          event.preventDefault();
          if (onThumbnailClick) {
            onThumbnailClick();
            return;
          }
          openPreview(event.currentTarget);
        }}
        data-hover-gallery-count={resolvedSources.length}
        data-hover-gallery-active-index={activeSourceIndex}
      >
        <img
          key={`${compactSource}:${retryGeneration}`}
          className={imageClassName}
          style={imageStyle}
          src={compactSource}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => handleCompactImageError(compactSource)}
        />
        {previewOpen ? (
          <Image.PreviewGroup
            key={retryGeneration}
            classNames={{
              popup: {
                root: `quote-image-preview-root ${styles.quoteImagePreview} ${
                  activeSourceIndex % 2 === 0
                    ? styles.previewImageEven
                    : styles.previewImageOdd
                }`,
              },
            }}
            items={resolvedSources.map((src) => ({ src, alt }))}
            fallback={`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
              '<svg xmlns="http://www.w3.org/2000/svg" width="640" height="420" viewBox="0 0 640 420"><rect width="640" height="420" rx="8" fill="#f3f3f3"/><g transform="translate(284 174) scale(3)" fill="none" stroke="#626262" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><path d="m13 2-3 7 4 4-3 9M3 16l4-4 3 3m5 1 3-4 3 4"/><circle cx="6.5" cy="7" r="1"/></g></svg>',
            )}`}
            preview={{
              open: true,
              current: activeSourceIndex,
              alt: previewAlt,
              focusTrap: true,
              closeIcon: <CloseOutlined aria-label="关闭图片预览" />,
              countRender: (current, total) => (
                <span aria-live="polite">
                  {current} / {total}
                </span>
              ),
              onChange: setActiveIndex,
              onOpenChange: setPreviewOpen,
            }}
          />
        ) : null}
      </span>
    </Popover>
  );
}
